import { toCalendarLocale } from '@/utils/dateUtils';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** The units the elapsed time is told in, coarsest first; the first that counts at least one wins. */
const ELAPSED_UNITS = [
    { unit: 'year', ms: 365 * DAY_MS },
    { unit: 'month', ms: 30 * DAY_MS },
    { unit: 'week', ms: 7 * DAY_MS },
    { unit: 'day', ms: DAY_MS },
    { unit: 'hour', ms: HOUR_MS },
    { unit: 'minute', ms: MINUTE_MS },
] as const satisfies readonly { unit: Intl.RelativeTimeFormatUnit; ms: number }[];

/**
 * How long ago a recognition was sent, written the way the reader's language writes it ("il y a
 * 3 jours", "hier"), together with the machine value the card's `<time>` carries.
 */
export const formatSentAt = (
    sentAt: string,
    language: string,
    now: number = Date.now(),
): { label: string; dateTime: string } => {
    const sentAtDate = new Date(sentAt);
    // A reader's clock a few seconds ahead of the server must not read as "in 3 seconds".
    const elapsed = Math.max(0, now - sentAtDate.getTime());
    const elapsedUnit = ELAPSED_UNITS.find((candidate) => elapsed >= candidate.ms);
    // Anything under a minute reads as "à l'instant" rather than counting seconds down.
    const { unit, value } = elapsedUnit
        ? { unit: elapsedUnit.unit, value: -Math.floor(elapsed / elapsedUnit.ms) }
        : { unit: 'second' as const, value: 0 };

    return {
        label: new Intl.RelativeTimeFormat(toCalendarLocale(language), { numeric: 'auto' }).format(
            value,
            unit,
        ),
        dateTime: sentAtDate.toISOString(),
    };
};
